import { USAGE_EVENT_FIELD_PATTERN } from '@monetize/constants';
import { BillingPlatformApiErrorKnownResponse } from '@monetize/types/common';
import { z } from 'zod';

export enum InvoiceTransformationConditionOperatorEnum {
  EQ = 'EQ',
  NE = 'NE',
  IN = 'IN',
  NOT_IN = 'NOT_IN',
  CONTAINS = 'CONTAINS',
}

export const InvoiceTransformationConditionOperatorEnumZ = z.nativeEnum(
  InvoiceTransformationConditionOperatorEnum,
);

export enum InvoiceTransformationTargetEnum {
  INVOICE_ITEM = 'INVOICE_ITEM',
  USAGE_EVENT = 'USAGE_EVENT',
}

export const InvoiceTransformationTargetEnumZ = z.nativeEnum(
  InvoiceTransformationTargetEnum,
);

/**
 * Output mode for an invoice transformation. Only one mode is allowed per bill
 * group at a time.
 * - MULTIPLE_INVOICES: split matching items into separate invoice documents.
 * - LINE_TRANSFORMATION: split a single line into multiple lines on one invoice.
 */
export enum InvoiceTransformationOutputModeEnum {
  MULTIPLE_INVOICES = 'MULTIPLE_INVOICES',
  LINE_TRANSFORMATION = 'LINE_TRANSFORMATION',
}

export const InvoiceTransformationOutputModeEnumZ = z.nativeEnum(
  InvoiceTransformationOutputModeEnum,
);

export const InvoiceTransformationAvailableFieldSchema = z.object({
  fieldPath: z.string(),
  description: z.string(),
  exampleValues: z.array(z.string()),
});
export type InvoiceTransformationAvailableField = z.infer<
  typeof InvoiceTransformationAvailableFieldSchema
>;

export const InvoiceTransformationConditionSchema = z.object({
  operator: InvoiceTransformationConditionOperatorEnumZ,
  field: z.string().trim().min(1, 'Field is required'),
  value: z.string().optional(),
  // Used for IN and NOT_IN operators
  values: z.array(z.string()).optional(),
});
export type InvoiceTransformationCondition = z.infer<
  typeof InvoiceTransformationConditionSchema
>;

export const InvoiceTransformationConditionGroupSchema = z.object({
  target: InvoiceTransformationTargetEnumZ,
  conditions: InvoiceTransformationConditionSchema.array(),
  groupBy: z.string().array().optional(),
});
export type InvoiceTransformationConditionGroup = z.infer<
  typeof InvoiceTransformationConditionGroupSchema
>;

export const InvoiceTransformationBillGroupOverrideSchema = z.object({
  purchaseOrderNumber: z.string().optional(),
  vatNumber: z.string().optional(),
  registrationNumber: z.string().optional(),
  locale: z.string().optional(),
  billingContactId: z.string().optional(),
  shippingContactId: z.string().optional(),
});
export type InvoiceTransformationBillGroupOverride = z.infer<
  typeof InvoiceTransformationBillGroupOverrideSchema
>;

/**
 * A single rule within a transformation. Matching invoice lines are grouped and
 * either split into separate invoices or transformed into multiple lines,
 * depending on the parent transformation's output mode.
 */
export const InvoiceLineRuleSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  priority: z
    .number()
    .int('Priority must be a non-negative integer')
    .nonnegative('Priority must be a non-negative integer'),
  conditionGroups: InvoiceTransformationConditionGroupSchema.array().min(
    1,
    'At least one condition group is required',
  ),
  // Only meaningful for MULTIPLE_INVOICES output mode.
  billGroupOverrides: InvoiceTransformationBillGroupOverrideSchema.optional(),
});
export type InvoiceLineRule = z.infer<typeof InvoiceLineRuleSchema>;

export const InvoiceTransformationOutputSchema = z.object({
  mode: InvoiceTransformationOutputModeEnumZ,
});
export type InvoiceTransformationOutput = z.infer<
  typeof InvoiceTransformationOutputSchema
>;

export const InvoiceTransformationSchema = z.object({
  output: InvoiceTransformationOutputSchema,
  invoiceLineRules: InvoiceLineRuleSchema.array(),
});
export type InvoiceTransformation = z.infer<typeof InvoiceTransformationSchema>;

/**
 * Request
 */
export const InvoiceTransformationRequestSchema = z.object({
  billGroupId: z.string(),
  invoiceTransformations: InvoiceTransformationSchema.array(),
});
export type InvoiceTransformationRequest = z.infer<
  typeof InvoiceTransformationRequestSchema
>;

/**
 * Response
 *
 * The BE returns `active` and `id` on each line rule. `active` is currently
 * server-managed (always true on create); not editable from the FE.
 */
export const InvoiceTransformationResponseSchema = z.object({
  billGroupId: z.string(),
  invoiceTransformations: InvoiceTransformationSchema.extend({
    invoiceLineRules: InvoiceLineRuleSchema.extend({
      id: z.string(),
      active: z.boolean(),
    }).array(),
  }).array(),
});
export type InvoiceTransformationResponse = z.infer<
  typeof InvoiceTransformationResponseSchema
>;

/**
 * UI Type for Form
 *
 * Only one output mode is allowed at a time, so the form holds a single `mode`
 * plus a flat list of line rules (kept shallow so RHF field paths stay simple).
 */
const MULTI_VALUE_OPERATORS_FOR_VALIDATION =
  new Set<InvoiceTransformationConditionOperatorEnum>([
    InvoiceTransformationConditionOperatorEnum.IN,
    InvoiceTransformationConditionOperatorEnum.NOT_IN,
  ]);

const USAGE_EVENT_FIELD_MESSAGE =
  'Must be a single identifier (letters, digits, underscores; starts with a letter)';

export interface InvoiceTransformationLimit {
  max: number;
  /** Plural noun used in limit messages, e.g. "condition sets". */
  noun: string;
}

/**
 * Maximum number of entries the UI lets a user configure at each level. Only
 * enforced by the UI schema: the response schema must still load
 * configurations saved before these limits existed.
 */
export const INVOICE_TRANSFORMATION_LIMITS = {
  invoiceLineRules: { max: 3, noun: 'rules' },
  conditionGroups: { max: 3, noun: 'condition sets' },
  conditions: { max: 3, noun: 'conditions' },
  groupBy: { max: 3, noun: 'group by fields' },
} as const satisfies Record<string, InvoiceTransformationLimit>;

export const getInvoiceTransformationLimitMessage = ({
  max,
  noun,
}: InvoiceTransformationLimit) => `A maximum of ${max} ${noun} is allowed.`;

const getOverLimitMessage = (limit: InvoiceTransformationLimit) =>
  `${getInvoiceTransformationLimitMessage(limit)} Remove the extra ${limit.noun} to save.`;

const InvoiceTransformationConditionGroupUiSchema =
  InvoiceTransformationConditionGroupSchema.extend({
    conditions: InvoiceTransformationConditionSchema.array().max(
      INVOICE_TRANSFORMATION_LIMITS.conditions.max,
      getOverLimitMessage(INVOICE_TRANSFORMATION_LIMITS.conditions),
    ),
    groupBy: z
      .string()
      .array()
      .max(
        INVOICE_TRANSFORMATION_LIMITS.groupBy.max,
        getOverLimitMessage(INVOICE_TRANSFORMATION_LIMITS.groupBy),
      )
      .optional(),
  });

export const InvoiceTransformationRequestUiSchema = z
  .object({
    billGroupId: z.string(),
    mode: InvoiceTransformationOutputModeEnumZ,
    invoiceLineRules: InvoiceLineRuleSchema.extend({
      conditionGroups: InvoiceTransformationConditionGroupUiSchema.array()
        .min(1, 'At least one condition group is required')
        .max(
          INVOICE_TRANSFORMATION_LIMITS.conditionGroups.max,
          getOverLimitMessage(INVOICE_TRANSFORMATION_LIMITS.conditionGroups),
        ),
      billGroupOverrides: z
        .object({
          key: InvoiceTransformationBillGroupOverrideSchema.keyof(),
          value: z.string().trim().min(1, 'Value is required'), // TODO: potentially other data types depending on the field
        })
        .array(),
    })
      .superRefine((rule, ctx) => {
        rule.conditionGroups.forEach((group, groupIndex) => {
          const isUsageEvent =
            group.target === InvoiceTransformationTargetEnum.USAGE_EVENT;

          const hasConditions = group.conditions.length > 0;
          const hasGroupBy = (group.groupBy ?? []).length > 0;
          if (!hasConditions && !hasGroupBy) {
            ctx.addIssue({
              code: 'custom',
              message:
                'Add at least one condition or group by field to this group',
              path: ['conditionGroups', groupIndex, 'conditions'],
            });
          }

          if (isUsageEvent) {
            (group.groupBy ?? []).forEach((entry, groupByIndex) => {
              if (!USAGE_EVENT_FIELD_PATTERN.test(entry)) {
                ctx.addIssue({
                  code: 'custom',
                  message: USAGE_EVENT_FIELD_MESSAGE,
                  path: [
                    'conditionGroups',
                    groupIndex,
                    'groupBy',
                    groupByIndex,
                  ],
                });
              }
            });
          }

          group.conditions.forEach((condition, conditionIndex) => {
            if (
              isUsageEvent &&
              condition.field &&
              !USAGE_EVENT_FIELD_PATTERN.test(condition.field)
            ) {
              ctx.addIssue({
                code: 'custom',
                message: USAGE_EVENT_FIELD_MESSAGE,
                path: [
                  'conditionGroups',
                  groupIndex,
                  'conditions',
                  conditionIndex,
                  'field',
                ],
              });
            }

            const isMulti = MULTI_VALUE_OPERATORS_FOR_VALIDATION.has(
              condition.operator,
            );
            if (isMulti) {
              if (!condition.values || condition.values.length === 0) {
                ctx.addIssue({
                  code: 'custom',
                  message: 'At least one value is required',
                  path: [
                    'conditionGroups',
                    groupIndex,
                    'conditions',
                    conditionIndex,
                    'values',
                  ],
                });
              }
            } else if (!condition.value?.trim()) {
              ctx.addIssue({
                code: 'custom',
                message: 'Value is required',
                path: [
                  'conditionGroups',
                  groupIndex,
                  'conditions',
                  conditionIndex,
                  'value',
                ],
              });
            }
          });
        });
      })
      .array()
      .max(
        INVOICE_TRANSFORMATION_LIMITS.invoiceLineRules.max,
        getOverLimitMessage(INVOICE_TRANSFORMATION_LIMITS.invoiceLineRules),
      ),
  })
  .superRefine((form, ctx) => {
    // Line transformations currently only support usage-event targets.
    if (form.mode !== InvoiceTransformationOutputModeEnum.LINE_TRANSFORMATION) {
      return;
    }
    form.invoiceLineRules.forEach((rule, ruleIndex) => {
      rule.conditionGroups.forEach((group, groupIndex) => {
        if (group.target !== InvoiceTransformationTargetEnum.USAGE_EVENT) {
          ctx.addIssue({
            code: 'custom',
            message: 'Line transformations only support usage event targets',
            path: [
              'invoiceLineRules',
              ruleIndex,
              'conditionGroups',
              groupIndex,
              'target',
            ],
          });
        }
      });
    });
  });
export type InvoiceTransformationRequestUi = z.infer<
  typeof InvoiceTransformationRequestUiSchema
>;

export type InvoiceTransformationResponseErrorContext = {
  errors: Array<
    | {
        errorCode:
          | 'BLANK_SPLIT_CONFIGURATION_NAME' // name is blank or whitespace-only
          | 'INVALID_SPLIT_CONFIGURATION_PRIORITY' // priority is negative
          | 'EMPTY_CONDITION_GROUPS' // no condition groups provided
          | 'EMPTY_CONDITIONS' // a condition group has no conditions
          | 'EMPTY_CONDITION_VALUES' // a condition has empty/blank value(s)
          | 'INVALID_FIELD_PATH' // field path is blank, has >3 segments, or references an unknown join root
          | 'DUPLICATE_SPLIT_CONFIGURATION_PRIORITY' // another existing definition uses this priority
          | 'DUPLICATE_PRIORITY_IN_BATCH'; // two or more definitions in the same request share the same priority
        details: string;
        context: Record<string, unknown>;
      }
    | {
        errorCode: 'INVALID_BILL_GROUP_OVERRIDE_REFERENCE'; // billingContactId / shippingContactId references a non-existent contact
        details: string;
        context: {
          overrideName: 'billingContactId' | 'shippingContactId';
          referenceValue: string;
          definitionName: string;
          definitionPriority: number;
        };
      }
  >;
};

export type InvoiceTransformationResponseError =
  BillingPlatformApiErrorKnownResponse<
    | InvoiceTransformationResponseErrorContext
    | { billGroupId: 'bg_nonexistent' } // bill group doesn't exist
  >;
